import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Users, Tag, ShieldCheck, BookOpen, ArrowRight, Sparkles } from "lucide-react";
import API from "../lib/api";
import AdminGuard from "../components/admin/AdminGuard";
import AdminShell from "../components/admin/AdminShell";

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="card-surface p-5" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-gradient-to-br from-[#E2C77A]/35 to-[#7A5F2A]/15 border border-[#C8A24A]/40 grid place-items-center shrink-0">
          <Icon className="w-4 h-4 gold-text" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.28em] uppercase gold-text">{label}</div>
          <div className="font-display text-2xl leading-none mt-1" data-testid={`${testId}-value`}>{value}</div>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboardPage() {
  const { t } = useTranslation();
  const [stats, setStats] = useState({ db_admins: "\u2014", env_admins: "\u2014", recent_actions: 0 });
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [usersRes, logRes] = await Promise.all([
          API.get("/admin/users"),
          API.get("/admin/audit-log?limit=10"),
        ]);
        setStats({
          db_admins: usersRes.data?.items?.length ?? 0,
          env_admins: usersRes.data?.env_allowlist?.length ?? 0,
          recent_actions: logRes.data?.items?.length ?? 0,
        });
        setRecent(logRes.data?.items || []);
      } catch {
        // soft-fail; non-admins will be blocked by AdminGuard anyway
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <AdminGuard>
      <AdminShell active="dashboard">
        <div className="space-y-8" data-testid="admin-dashboard">
          <div>
            <div className="eyebrow mb-2">{t("admin.dashboard.eyebrow", "\u2014 Overview")}</div>
            <h2 className="font-display text-2xl md:text-3xl leading-tight" data-testid="admin-dashboard-title">
              {t("admin.dashboard.title", "Admin Dashboard")}
            </h2>
            <p className="text-sm text-white/65 mt-2 max-w-2xl leading-relaxed">
              {t("admin.dashboard.subtitle", "Quick overview of the DONiTSH!T ecosystem.")}
            </p>
          </div>

          {/* Stats */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard icon={ShieldCheck} label={t("admin.dashboard.stat_db_admins", "DB Admins")} value={loading ? "\u2026" : stats.db_admins} testId="stat-db-admins" />
            <StatCard icon={Users} label={t("admin.dashboard.stat_env_admins", "ENV Admins")} value={loading ? "\u2026" : stats.env_admins} testId="stat-env-admins" />
            <StatCard icon={Sparkles} label={t("admin.dashboard.stat_recent_actions", "Recent Actions")} value={loading ? "\u2026" : stats.recent_actions} testId="stat-recent-actions" />
          </div>

          {/* Shortcuts */}
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/admin/users" className="card-surface p-5 hover:border-[#C8A24A]/40 transition-colors group" data-testid="shortcut-users">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md bg-gradient-to-br from-[#E2C77A]/35 to-[#7A5F2A]/15 border border-[#C8A24A]/40 grid place-items-center shrink-0">
                  <ShieldCheck className="w-4 h-4 gold-text" />
                </div>
                <div className="flex-1">
                  <div className="font-display text-lg group-hover:gold-text transition-colors">{t("admin.users.title", "Admin Users")}</div>
                  <p className="text-sm text-white/65 mt-1 leading-relaxed">
                    {t("admin.users.subtitle_short", "Promote or demote admins from the DB allowlist.")}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 opacity-50 group-hover:opacity-100 group-hover:gold-text transition-colors" />
              </div>
            </Link>
            <Link to="/admin/offers" className="card-surface p-5 hover:border-[#C8A24A]/40 transition-colors group" data-testid="shortcut-offers">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md bg-gradient-to-br from-[#E2C77A]/35 to-[#7A5F2A]/15 border border-[#C8A24A]/40 grid place-items-center shrink-0">
                  <Tag className="w-4 h-4 gold-text" />
                </div>
                <div className="flex-1">
                  <div className="font-display text-lg group-hover:gold-text transition-colors">{t("admin.dashboard.shortcut_offers", "Business Offers")}</div>
                  <p className="text-sm text-white/65 mt-1 leading-relaxed">
                    {t("admin.dashboard.shortcut_offers_sub", "Approve, feature, hide, or expire partner offers.")}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 opacity-50 group-hover:opacity-100 group-hover:gold-text transition-colors" />
              </div>
            </Link>
          </div>

          {/* Recent activity */}
          <div className="card-surface p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-lg">{t("admin.dashboard.recent_title", "Recent activity")}</div>
              <div className="text-[10px] tracking-[0.28em] uppercase gold-text">{t("admin.dashboard.recent_eyebrow", "Audit Log")}</div>
            </div>
            {loading ? (
              <div className="text-sm text-white/60">{t("admin.loading", "Loading\u2026")}</div>
            ) : recent.length === 0 ? (
              <div className="text-sm text-white/60" data-testid="recent-empty">{t("admin.dashboard.recent_empty", "No activity yet.")}</div>
            ) : (
              <ul className="divide-y divide-white/5" data-testid="recent-list">
                {recent.map((it, idx) => (
                  <li key={it.audit_id || idx} className="py-2.5 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-sm text-white/90"><span className="gold-text">{it.action}</span>{it.target ? <> · <span className="text-white/75">{it.target}</span></> : null}</div>
                      <div className="text-xs text-white/50 mt-0.5">{it.actor_email || t("admin.dashboard.system", "system")}</div>
                    </div>
                    <div className="text-xs text-white/45 whitespace-nowrap">{(it.created_at || "").replace("T", " ").slice(0, 19)}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Phase 7 roadmap nudge */}
          <div className="card-surface p-5 border-dashed">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-gradient-to-br from-[#E2C77A]/25 to-[#7A5F2A]/10 border border-[#C8A24A]/30 grid place-items-center shrink-0">
                <BookOpen className="w-4 h-4 gold-text" />
              </div>
              <div>
                <div className="font-display text-base">{t("admin.dashboard.roadmap_title", "Content Manager + Ask Donny Knowledge Base")}</div>
                <p className="text-sm text-white/65 mt-1 leading-relaxed">
                  {t("admin.dashboard.roadmap_sub", "Coming in Phase 7.2\u20137.4 \u2014 Markdown content authoring, document import (PDF/DOCX/MD/TXT), and a retrieval-grounded knowledge base for Donny.")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </AdminShell>
    </AdminGuard>
  );
}
